import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import ProductCard from "../components/ProductCard";
import { fetchProducts } from "../lib/api";

const SORT_OPTIONS = [
  { v: "newest", l: "Newest" },
  { v: "bestseller", l: "Best Sellers" },
  { v: "price_asc", l: "Price · Low to High" },
  { v: "price_desc", l: "Price · High to Low" },
  { v: "rating", l: "Top Rated" },
];

const CATEGORIES = [
  { slug: "necklaces", name: "Necklaces" },
  { slug: "earrings", name: "Earrings" },
  { slug: "bangles", name: "Bangles" },
  { slug: "rings", name: "Rings" },
];

const COLLECTIONS = [
  { slug: "rajputana", name: "Rajputana" },
  { slug: "bridal", name: "Bridal" },
  { slug: "antitarnish", name: "Anti-Tarnish" },
  { slug: "temple", name: "Temple" },
  { slug: "kundan", name: "Kundan" },
  { slug: "festive", name: "Festive" },
  { slug: "bestsellers", name: "Best Sellers" },
  { slug: "newarrivals", name: "New Arrivals" },
  { slug: "under499", name: "Under ₹499" },
  { slug: "under999", name: "Under ₹999" },
];

export default function Shop() {
  const [params, setParams] = useSearchParams();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  const collection = params.get("collection") || "";
  const category = params.get("category") || "";
  const q = params.get("q") || "";
  const sort = params.get("sort") || "newest";

  useEffect(() => {
    setLoading(true);
    fetchProducts({ collection, category, q, sort, limit: 60 })
      .then(setProducts).finally(() => setLoading(false));
  }, [collection, category, q, sort]);

  const activeTitle = useMemo(() => {
    if (q) return `Results for "${q}"`;
    if (collection) return COLLECTIONS.find((c) => c.slug === collection)?.name || "Collection";
    if (category) return CATEGORIES.find((c) => c.slug === category)?.name || "Category";
    return "All Jewellery";
  }, [q, collection, category]);

  const set = (key, val) => {
    const next = new URLSearchParams(params);
    if (val) next.set(key, val); else next.delete(key);
    // Only one of collection/category at a time
    if (key === "collection") next.delete("category");
    if (key === "category") next.delete("collection");
    setParams(next);
  };

  return (
    <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-14" data-testid="shop-page">
      <div className="mb-10">
        <p className="label-tiny text-brand-gold">Bindni Shop</p>
        <h1 className="font-serif-brand text-4xl lg:text-5xl text-brand-charcoal mt-2" data-testid="shop-title">{activeTitle}</h1>
        <p className="text-sm text-brand-muted mt-2">{products.length} pieces</p>
      </div>

      <div className="grid lg:grid-cols-[240px_1fr] gap-10">
        {/* Filters */}
        <aside className="space-y-8">
          <div>
            <p className="label-tiny text-brand-charcoal mb-4">Collections</p>
            <ul className="space-y-2 text-sm">
              <li>
                <button onClick={() => set("collection", "")} className={`hover:text-brand-maroon ${!collection ? "text-brand-maroon font-semibold" : "text-brand-charcoal"}`} data-testid="filter-collection-all">All</button>
              </li>
              {COLLECTIONS.map((c) => (
                <li key={c.slug}>
                  <button onClick={() => set("collection", c.slug)} className={`hover:text-brand-maroon ${collection === c.slug ? "text-brand-maroon font-semibold" : "text-brand-charcoal"}`} data-testid={`filter-collection-${c.slug}`}>
                    {c.name}
                  </button>
                </li>
              ))}
            </ul>
          </div>
          <div className="gold-divider" />
          <div>
            <p className="label-tiny text-brand-charcoal mb-4">Categories</p>
            <ul className="space-y-2 text-sm">
              <li>
                <button onClick={() => set("category", "")} className={`hover:text-brand-maroon ${!category ? "text-brand-maroon font-semibold" : "text-brand-charcoal"}`} data-testid="filter-category-all">All</button>
              </li>
              {CATEGORIES.map((c) => (
                <li key={c.slug}>
                  <button onClick={() => set("category", c.slug)} className={`hover:text-brand-maroon ${category === c.slug ? "text-brand-maroon font-semibold" : "text-brand-charcoal"}`} data-testid={`filter-category-${c.slug}`}>
                    {c.name}
                  </button>
                </li>
              ))}
            </ul>
          </div>
        </aside>

        <div>
          <div className="flex flex-wrap items-center justify-between gap-4 mb-8 pb-4 border-b border-brand-gold/25">
            <div className="flex flex-wrap gap-2">
              {collection && <span className="label-tiny bg-brand-maroon text-brand-ivory px-3 py-1">Collection: {collection} <button className="ml-2" onClick={() => set("collection", "")}>×</button></span>}
              {category && <span className="label-tiny bg-brand-maroon text-brand-ivory px-3 py-1">Category: {category} <button className="ml-2" onClick={() => set("category", "")}>×</button></span>}
              {q && <span className="label-tiny bg-brand-maroon text-brand-ivory px-3 py-1">"{q}" <button className="ml-2" onClick={() => set("q", "")}>×</button></span>}
            </div>
            <div className="flex items-center gap-3">
              <span className="label-tiny text-brand-muted">Sort</span>
              <select value={sort} onChange={(e) => set("sort", e.target.value)} className="text-sm bg-transparent border border-brand-gold/40 px-3 py-2 outline-none" data-testid="sort-select">
                {SORT_OPTIONS.map((o) => <option key={o.v} value={o.v}>{o.l}</option>)}
              </select>
            </div>
          </div>

          {loading ? (
            <div className="grid grid-cols-2 lg:grid-cols-3 gap-x-4 gap-y-10">
              {[...Array(6)].map((_, i) => <div key={i} className="aspect-[4/5] bg-brand-ivory-soft animate-pulse" />)}
            </div>
          ) : products.length === 0 ? (
            <div className="text-center py-24">
              <p className="font-serif-brand text-2xl text-brand-charcoal">No pieces match your search</p>
              <p className="text-sm text-brand-muted mt-2">Try another category or reach out on WhatsApp.</p>
            </div>
          ) : (
            <div className="grid grid-cols-2 lg:grid-cols-3 gap-x-4 lg:gap-x-8 gap-y-12">
              {products.map((p) => <ProductCard key={p.id} product={p} />)}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
